import React, { useEffect, useState } from 'react';
import AppSearchBar from '../components/common/input/AppSearchBar';
import {
    useLazyGetPublicProfileQuery,
    useLazyGetPublicTeamQuery,
} from '../app/services/api';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    clearProfiles,
    setPublicProfile,
    setPublicTeam,
} from '../store/profileSlice';
import { clearCheckoutDetails } from '../store/checkoutSlice';
import AppHeader from '../components/common/layout/AppHeader';
import AppButton from '../components/common/input/AppButton';
import MainContainer from '../components/common/layout/MainContainer';
import WelcomeScreenAnimation from '../components/common/layout/WelcomeScreenAnimation';

const Search: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [noUserFound, setNoUserFound] = useState<boolean>(false);
    const [isNextEnabled, setIsNextEnabled] = useState<boolean>(false);

    const [getPublicProfileTrigger] = useLazyGetPublicProfileQuery();
    const [getPublicTeamTrigger] = useLazyGetPublicTeamQuery();

    useEffect(() => {
        dispatch(clearProfiles());
        dispatch(clearCheckoutDetails());
    }, [dispatch]);

    const processSearch = async (searchValue: string) => {
        setIsSearching(true);

        let publicProfileFound = false;
        let publicTeamFound = false;

        const profileTriggerErrorHandler = (e: any): void => {
            if (e.status === 404) {
                setErrorMessage('ID not found. Please check and try again.');
            } else {
                setErrorMessage('Something went wrong, please try again...');
            }
        }

        await getPublicProfileTrigger(searchValue)
            .unwrap()
            .then((res) => {
                publicProfileFound = true;
                dispatch(setPublicProfile(res.content));
                setErrorMessage('');
            })
            .catch((e) => {
                profileTriggerErrorHandler(e);
            });

        if (!publicProfileFound) {
            await getPublicTeamTrigger(searchValue)
                .unwrap()
                .then((res) => {
                    publicTeamFound = true;
                    dispatch(setPublicTeam(res.content));
                    setErrorMessage('');
                })
                .catch((e) => {
                    profileTriggerErrorHandler(e);
                })
        }

        setNoUserFound(!publicProfileFound && !publicTeamFound);
        setIsNextEnabled(publicProfileFound || publicTeamFound);
        setIsSearching(false);
    }

    const onSearchQueryChange = async (searchQuery: string) => {
        setSearchQuery(searchQuery);
        const isValid = searchQuery.match(/^\d{6}$/g);

        if (!isValid) {
            setErrorMessage('ID Code must be 6 digits');
            setIsNextEnabled(false);
            return;
        }

        await processSearch(searchQuery);
    };

    const navigateNext: any = (): void => {
        navigate(`/${searchQuery}`);

        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        });
    };

    return (
        <MainContainer className="bg-sky-blue max-md:fixed max-md:inset-0 max-md:h-[100dvh] max-md:min-h-0">
            <AppHeader
                isBackIconShown={false}
                className="!bg-sky-blue md:!bg-white lg:hidden md:mb-6 md:!p-0 md:!h-10"
            ></AppHeader>
            <main className="flex-1 flex relative bg-sky-blue md:h-56 md:flex-none md:overflow-hidden md:rounded-3xl">
                <WelcomeScreenAnimation className="scale-150 absolute bottom-7 -left-48 md:scale-100 md:bottom-0 md:-left-52" />
            </main>
            <div className="rounded-t-3xl bg-white z-10 px-4 pt-10 pb-10 md:rounded-none md:px-0 md:pt-6 md:pb-0 xl:pt-10">
                {noUserFound ? (
                    <h6 className="mx-3 text-center transition duration-200 text-pressed-blue">
                        We couldn't find the person your trying to tip. You can
                        try a different ID code below.
                    </h6>
                ) : (
                    <h6 className="mx-3 text-center transition duration-200 text-pressed-blue">
                        Send quick, cashless tips to workers you wish to thank!
                    </h6>
                )}

                <p className="px-4 mt-10 font-light text-center text-pressed-blue">
                    Enter the ID code to send a tip
                </p>

                <div className="w-full mt-2 mb-10 relative">
                    <AppSearchBar
                        isEnabled={!isSearching}
                        placeholder="e.g. 140636"
                        setSearchQuery={onSearchQueryChange}
                        className="w-full"
                        inputMode='numeric'
                        maxLength={6}
                    />
                    {errorMessage && (
                        <div className="flex items-center align-middle text-xs absolute left-8 -bottom-[7px] text-error blurred-background bg-white px-[3px]">
                            {errorMessage}
                        </div>
                    )}
                </div>

                <AppButton
                    sizeClassName="w-full h-12"
                    value="Next"
                    isEnabled={isNextEnabled}
                    onClick={navigateNext}
                />
            </div>
        </MainContainer>
    );
};

export default Search;
